/**
 * Часть 2 — что делает вегетация прямо сейчас.
 *
 * Everything here is READ from cell 2's own output (`payload.optical_state`,
 * pipeline/current_state.py build_result_60d): the direction class, the position relative to
 * the 60-day local maximum, and how old the freshest clear acquisition is. Nothing is
 * re-derived from the raw numbers — the classes are the pipeline's decision and this module
 * only decides how to word them.
 *
 * The one mapping made here is class code -> up/down/flat, and it is a mapping of the
 * server's own class NAMES, not a re-test of any threshold: the numeric branches all live in
 * current_state.classify_direction_60d and must stay there.
 */

import { T } from "../strings";
import type { OpticalState, Run } from "../api/types";
import { formatDayMonth, isFiniteNumber } from "./format";
import { toneOf, type StripTone } from "./strip";

export type VegetationTrend = "rising" | "falling" | "flat" | "unknown";

/** classify_direction_60d codes, grouped for the arrow and the accent colour only. */
const RISING_CLASSES = new Set([
  "EARLY_EMERGENCE",
  "EARLY_ACTIVE_GROWTH",
  "ACTIVE_GROWTH",
  "STRONG_GROWTH_NEAR_MAXIMUM",
]);

const FALLING_CLASSES = new Set([
  "DECLINING_FROM_HIGH_LEVEL",
  "ACTIVE_DECLINE",
  "LOW_AFTER_DECLINE",
  "VERY_LOW_AFTER_DECLINE",
  "RAPID_VEGETATION_REMOVAL_OR_DRYING",
]);

const FLAT_CLASSES = new Set([
  "HIGH_PLATEAU_OR_LOCAL_PEAK",
  "MODERATE_HIGH_STABLE",
  "MODERATE_STABLE",
  "LOW_STABLE",
  "VERY_LOW_OR_BARE",
]);

export const trendOfClass = (code: string | null | undefined): VegetationTrend => {
  if (!code) return "unknown";
  if (RISING_CLASSES.has(code)) return "rising";
  if (FALLING_CLASSES.has(code)) return "falling";
  if (FLAT_CLASSES.has(code)) return "flat";
  return "unknown";
};

/* Стрелки направления БОЛЬШЕ НЕТ. Она была единственным текстовым глифом на экране —
 * иконки продукта рисуются штрихом на сетке 24 — и несла то, что стоящая рядом строка уже
 * говорила словом. Второй носитель одного факта не делает его читаемее, он делает экран
 * шумнее. `trend` остаётся: на нём держится цвет, а цвет здесь повтор, а не носитель. */

export const TREND_COLOR_VAR: Record<VegetationTrend, string> = {
  rising: "var(--state-veg)",
  falling: "var(--state-bare)",
  flat: "var(--state-stubble)",
  unknown: "var(--state-nodata)",
};

export interface VegetationView {
  /** vegetation_state_ru — the notebook's own wording, shown as the explanation. */
  sentence: string | null;
  trend: VegetationTrend;
  colorVar: string;
  /** Position relative to the local 60-day maximum, in words. */
  positionLabel: string | null;
  ndviNow: number | null;
  change30: number | null;
  peakDate: string | null;
  peakNdvi: number | null;
  daysFromPeak: number | null;
  dropFromPeak: number | null;
  acceptedDates: number | null;
  latestClearDate: string | null;
  ageDays: number | null;
  stale: boolean;
  removalSupported: boolean;
  supportFlags: string[];
}

const label = (
  dictionary: Record<string, string>,
  code: string | null | undefined,
): string | null => {
  if (!code) return null;
  return dictionary[code] ?? null;
};

const positionLabels = T.vegetation.positions as Record<string, string>;
// `confidenceLabels` УДАЛЕНЫ 2026-09-05 вместе с `vegetation.confidenceLevels` (решение
// владельца, (правила текстов — ТЗ.md): «уверенность измерения» — внутренняя шкала.
const supportLabels = T.vegetation.supportFlags as Record<string, string>;

const supportFlagsOf = (state: OpticalState): string[] => {
  const raw = state.supporting_index_flags;
  const codes = Array.isArray(raw)
    ? raw.filter((code): code is string => typeof code === "string")
    : [];
  return codes.map((code) => supportLabels[code] ?? code);
};

/**
 * The picture of the vegetation, or null when the run produced no cell-2 state at all —
 * in which case the component says WHY, via `vegetationGapReason`.
 */
export const vegetationFromRun = (run: Run | null | undefined): VegetationView | null => {
  if (!run || run.status !== "succeeded") return null;
  const state = run.payload?.optical_state ?? null;
  if (state === null) return null;

  const code = state.vegetation_state_class ?? null;
  const trend = trendOfClass(code);
  const result = run.result ?? null;
  const ndviNow =
    result !== null && isFiniteNumber(result.current_ndvi)
      ? result.current_ndvi
      : isFiniteNumber(state.robust_current_ndvi)
        ? state.robust_current_ndvi
        : null;

  return {
    // `headline` УДАЛЁН 2026-09-05 вместе с шестнадцатью классами состояния из словаря: блок
    // ② потерял свой заголовок ещё в переделке консоли, и с тех пор это поле не читал никто,
    // кроме собственных тестов. Ход растительности говорит теперь одна строка над карточкой,
    // и ключуется она `trend`, который считается здесь же и остаётся на месте.
    sentence: state.vegetation_state_ru ?? null,
    trend,
    colorVar: TREND_COLOR_VAR[trend],
    // `local_position_ru` НЕ подставляется запасным вариантом: серверная проза («положение
    // относительно локального максимума не подтверждено») — ярус 3, и на продуктовую
    // поверхность она не выходит (правила текстов — ТЗ.md). Нет кода — нет строки.
    positionLabel: label(positionLabels, state.local_position_code),
    ndviNow,
    change30: isFiniteNumber(state.ndvi_change_per_30d) ? state.ndvi_change_per_30d : null,
    peakDate: state.local_peak_date_60d ?? null,
    peakNdvi: isFiniteNumber(state.local_peak_ndvi_60d) ? state.local_peak_ndvi_60d : null,
    daysFromPeak: isFiniteNumber(state.days_from_local_peak) ? state.days_from_local_peak : null,
    dropFromPeak: isFiniteNumber(state.drop_from_local_peak) ? state.drop_from_local_peak : null,
    acceptedDates: isFiniteNumber(state.accepted_unique_dates) ? state.accepted_unique_dates : null,
    latestClearDate: state.latest_clear_date ?? result?.latest_optical_date ?? null,
    ageDays: isFiniteNumber(state.data_age_days)
      ? state.data_age_days
      : result !== null && isFiniteNumber(result.optical_age_days)
        ? result.optical_age_days
        : null,
    stale: state.current_data_is_stale === true,
    removalSupported: state.rapid_removal_supported === true,
    supportFlags: supportFlagsOf(state),
  };
};

/**
 * ЗОНА ПОВЕРХНОСТИ — растительность / стерня / открытая почва на последнем чистом снимке.
 * Имя концепции там, где её читают как зону; тип — тот же самый, что у колонки полосы, и
 * второго набора границ под него не заведено.
 */
export type SurfaceZone = StripTone;

/**
 * Зона последнего чистого снимка — ровно та, которую полоска уровня называет СЛОВОМ.
 *
 * Считается ЧЕРЕЗ `vegetationFromRun` и `toneOf`, а не заново по числу, и это условие, а не
 * удобство: границы 0.25 / 0.35 живут в `lib/constants.ts` в единственном экземпляре, а
 * «зона есть» здесь означает буквально то же, что «полоска нарисована» (`LevelBar` требует и
 * NDVI, и дату снимка). Иначе тон карточки объяснялся бы строкой, которой на экране нет.
 */
export const surfaceZoneOf = (run: Run | null | undefined): SurfaceZone => {
  const view = vegetationFromRun(run);
  if (view === null || view.ndviNow === null || view.latestClearDate === null) return "unknown";
  return toneOf(view.ndviNow);
};

/**
 * ХОД РАСТИТЕЛЬНОСТИ — одна строка НАД карточкой ответа.
 *
 * Карточка отвечает, что с полем СДЕЛАЛИ, и не отвечает, что на нём ПРОИСХОДИТ (замечание
 * агронома). Строка говорит второе и только его: подлежащее у неё — растительность, а слова
 * заголовков карточки ей запрещены поимённо (`strings.test.ts`), потому что стоящая выше
 * заголовка строка с его же словарём читалась бы вторым ответом на тот же вопрос.
 *
 * Ни одного нового решения о поле здесь не принято: `trend` считается из класса сервера тем
 * же `trendOfClass`, что и раньше красил блок цветом, а зона — та самая, которую полоска
 * уровня называет словом. Таблица написана полными путями словаря — как `TREND_COLOR_VAR`
 * рядом, — чтобы сторож мёртвых ключей видел эти три строки сам.
 *
 * МОЛЧИТ строка в ПЯТИ случаях из семи, и они здесь: нет вида, нет даты снимка, снимки
 * устарели, незнакомый серверу класс и ровный ход на открытой почве. Ещё два — состояния
 * колонки (`Console`): вердикта нет вовсе и вердикт запечатан. Счёт держится в комментарии
 * нарочно: он и есть способ заметить, что молчаний стало больше, чем кто-то помнил.
 */
const DYNAMICS_BY_TREND: Record<VegetationTrend, ((date: string) => string) | null> = {
  rising: T.vegetation.dynamics.rising,
  falling: T.vegetation.dynamics.falling,
  flat: T.vegetation.dynamics.flat,
  // Незнакомый серверу класс — не «ход не определён», а отсутствие наблюдения: подписи под
  // пустым местом продукт не печатает.
  unknown: null,
};

export const dynamicsLine = (
  view: VegetationView | null | undefined,
  zone: SurfaceZone,
): string | null => {
  // Дата — ТОТ ЖЕ `latestClearDate`, что стоит в строке полоски уровня: две даты об одном
  // снимке на одном экране читались бы как два снимка (UX_SPEC §3.3).
  if (!view || view.latestClearDate === null) return null;
  // Снимки слишком старые — о ходе по ним не говорят: об этом уже сказано нотой в блоке
  // доказательства, а «растёт» рядом с ней было бы утверждением о сегодняшнем дне.
  if (view.stale) return null;
  // РОВНЫЙ ХОД НА ОТКРЫТОЙ ПОЧВЕ МОЛЧИТ (2026-10-09, сверка словаря состояний). Про ход там
  // сказать нечего — движения нет, — а состояние уже названо трижды: полоской уровня,
  // заголовком карточки и строкой доказательства. Четвёртая копия того же датированного факта
  // и была жалобой на «разные слова об одном состоянии»; `flat` сюда тоже не подставляется —
  // «растительность не меняется» утверждало бы, что что-то стоит и держится.
  if (view.trend === "flat" && zone === "bare") return null;
  const line = DYNAMICS_BY_TREND[view.trend];
  return line === null ? null : line(formatDayMonth(view.latestClearDate));
};

/** Why part 2 has nothing to show. Never a blank space — the operator gets a next step. */
export const vegetationGapReason = (run: Run): string => {
  if (run.status === "failed") {
    return T.reason.byCode(run.error_reason);
  }
  const audit = run.payload?.optical_audit ?? [];
  if (audit.length === 0) return T.vegetation.noAcquisitions;
  // Счёт отклонённых сюда больше не подставляется: знаменатель на экране один, и печатает
  // его блок снимков (UX_SPEC §3.5).
  if (audit.every((row) => !row.accepted)) return T.reason.noAcceptedDates;
  return T.vegetation.stateMissing;
};
